import React, { useState, useEffect } from 'react';
import { Plus, BarChart2, Trash2, Edit3, X, History } from 'lucide-react';
import { assetsAPI, priceHistoryAPI } from '../api/client';
import { useInvestmentContext } from '../context/InvestmentContext';

const ASSET_TYPES = [
  { value: 'stock', label: 'Stock' },
  { value: 'etf', label: 'ETF' },
  { value: 'bond', label: 'Bond' },
  { value: 'mutual_fund', label: 'Mutual Fund' },
  { value: 'fund', label: 'Fondo' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'commodity', label: 'Commodity' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'COP', 'ARS', 'MXN', 'BRL'];

// ── Asset Form Modal ───────────────────────────────────────────────────────────

const AssetForm = ({ asset, onSubmit, onClose }) => {
  const [form, setForm] = useState({
    symbol: asset?.symbol || '',
    name: asset?.name || '',
    asset_type: asset?.asset_type || 'stock',
    currency: asset?.currency || 'USD',
    sector: asset?.sector || '',
    country: asset?.country || '',
    description: asset?.description || '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.symbol.trim()) errs.symbol = 'Symbol is required.';
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrors({});
    try {
      await onSubmit({
        symbol: form.symbol.trim().toUpperCase(),
        name: form.name.trim(),
        asset_type: form.asset_type,
        currency: form.currency,
        sector: form.sector.trim() || null,
        country: form.country.trim() || null,
        description: form.description.trim() || null,
      });
    } catch (err) {
      setErrors(err.response?.data || {});
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-gray-900">
            {asset ? 'Edit Asset' : 'New Asset'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Symbol */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ticker Symbol</label>
            <input
              type="text"
              value={form.symbol}
              onChange={(e) => setField('symbol', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none uppercase ${errors.symbol ? 'border-red-400' : 'border-gray-200'}`}
              placeholder="e.g. AAPL"
            />
            {errors.symbol && <p className="mt-1 text-xs text-red-500">{errors.symbol}</p>}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${errors.name ? 'border-red-400' : 'border-gray-200'}`}
              placeholder="e.g. Apple Inc."
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
          </div>

          {/* Asset Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="grid grid-cols-3 gap-2">
              {ASSET_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setField('asset_type', t.value)}
                  className={`py-2 text-xs font-medium rounded-lg transition ${
                    form.asset_type === t.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <select
              value={form.currency}
              onChange={(e) => setField('currency', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Sector & Country */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sector (optional)</label>
              <input
                type="text"
                value={form.sector}
                onChange={(e) => setField('sector', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. Technology"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country (optional)</label>
              <input
                type="text"
                value={form.country}
                onChange={(e) => setField('country', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="e.g. US"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
              rows={2}
              placeholder="Brief description..."
            />
          </div>

          {errors.non_field_errors && (
            <p className="text-xs text-red-500">{errors.non_field_errors}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : asset ? 'Save Changes' : 'Create Asset'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Price History Modal ────────────────────────────────────────────────────────

const emptyForm = { date: new Date().toISOString().slice(0, 10), close_price: '', open_price: '', high_price: '', low_price: '', volume: '' };

const PriceHistoryModal = ({ asset, onClose }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    priceHistoryAPI.list(asset.id)
      .then((res) => setRecords(Array.isArray(res.data) ? res.data : (res.data.results ?? [])))
      .finally(() => setLoading(false));
  }, [asset.id]);

  const setField = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.date) errs.date = 'Required.';
    if (!form.close_price || parseFloat(form.close_price) <= 0) errs.close_price = 'Required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    try {
      const payload = {
        asset: asset.id,
        date: form.date,
        close_price: parseFloat(form.close_price),
        open_price: form.open_price ? parseFloat(form.open_price) : null,
        high_price: form.high_price ? parseFloat(form.high_price) : null,
        low_price: form.low_price ? parseFloat(form.low_price) : null,
        volume: form.volume ? parseInt(form.volume) : null,
      };
      const res = await priceHistoryAPI.create(payload);
      setRecords((prev) => [res.data, ...prev].sort((a, b) => b.date.localeCompare(a.date)));
      setForm(emptyForm);
      setShowForm(false);
      setErrors({});
    } catch (err) {
      setErrors(err.response?.data || { non_field_errors: 'Error saving record.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await priceHistoryAPI.delete(id);
    setRecords((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Price History</h2>
            <p className="text-sm text-gray-500">{asset.symbol} · {asset.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowForm((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Add Form */}
        {showForm && (
          <form onSubmit={handleAdd} className="p-4 border-b border-gray-100 bg-blue-50 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setField('date', e.target.value)}
                  className={`w-full px-2.5 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${errors.date ? 'border-red-400' : 'border-gray-200'}`}
                />
                {errors.date && <p className="mt-0.5 text-xs text-red-500">{errors.date}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Close *</label>
                <input
                  type="number"
                  value={form.close_price}
                  onChange={(e) => setField('close_price', e.target.value)}
                  className={`w-full px-2.5 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${errors.close_price ? 'border-red-400' : 'border-gray-200'}`}
                  placeholder="0.00" min="0" step="0.01"
                />
                {errors.close_price && <p className="mt-0.5 text-xs text-red-500">{errors.close_price}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Open</label>
                <input
                  type="number" value={form.open_price}
                  onChange={(e) => setField('open_price', e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  placeholder="0.00" min="0" step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">High</label>
                <input
                  type="number" value={form.high_price}
                  onChange={(e) => setField('high_price', e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  placeholder="0.00" min="0" step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Low</label>
                <input
                  type="number" value={form.low_price}
                  onChange={(e) => setField('low_price', e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  placeholder="0.00" min="0" step="0.01"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Volume</label>
                <input
                  type="number" value={form.volume}
                  onChange={(e) => setField('volume', e.target.value)}
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                  placeholder="0" min="0" step="1"
                />
              </div>
            </div>
            {errors.non_field_errors && <p className="text-xs text-red-500">{errors.non_field_errors}</p>}
            {errors.asset && <p className="text-xs text-red-500">{errors.asset}</p>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => { setShowForm(false); setErrors({}); }}
                className="px-4 py-1.5 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}

        {/* Records Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <History className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No price history yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Date', 'Open', 'Close', 'High', 'Low', 'Volume', ''].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-medium text-gray-800">{r.date}</td>
                    <td className="px-4 py-3 text-gray-600">{r.open_price ?? '—'}</td>
                    <td className="px-4 py-3 font-semibold text-gray-900">{r.close_price}</td>
                    <td className="px-4 py-3 text-gray-600">{r.high_price ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.low_price ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{r.volume != null ? r.volume.toLocaleString() : '—'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleDelete(r.id)}
                        className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Assets Screen ──────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  stock: 'bg-blue-100 text-blue-700',
  etf: 'bg-green-100 text-green-700',
  bond: 'bg-yellow-100 text-yellow-700',
  mutual_fund: 'bg-purple-100 text-purple-700',
  fund: 'bg-indigo-100 text-indigo-700',
  crypto: 'bg-orange-100 text-orange-700',
  commodity: 'bg-gray-100 text-gray-700',
};

const AssetsScreen = () => {
  const { assets, assetsLoading, createAsset, fetchAssets } = useInvestmentContext();
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [historyAsset, setHistoryAsset] = useState(null);

  const handleCreate = async (data) => {
    await createAsset(data);
    setShowForm(false);
  };

  const handleUpdate = async (data) => {
    try {
      await assetsAPI.update(editingAsset.id, data);
      await fetchAssets();
      setEditingAsset(null);
      setShowForm(false);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async (id) => {
    try {
      await assetsAPI.delete(id);
      await fetchAssets();
    } catch {
      setError('Failed to delete asset.');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 pt-8 pb-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
            <p className="text-gray-500 text-sm">{assets.length} asset{assets.length !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => { setEditingAsset(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> New Asset
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-medium">Dismiss</button>
          </div>
        )}

        {assetsLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : assets.length === 0 ? (
          <div className="text-center py-16">
            <BarChart2 className="w-14 h-14 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No assets yet</p>
            <p className="text-gray-400 text-sm mt-1">Add assets to start recording transactions</p>
            <button
              onClick={() => { setEditingAsset(null); setShowForm(true); }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition"
            >
              Add Asset
            </button>
          </div>
        ) : (
          assets.map((asset) => (
            <div key={asset.id} className="bg-white rounded-2xl shadow-sm p-5 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                  <span className="text-xs font-bold text-blue-600">{asset.symbol}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{asset.name}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_COLORS[asset.asset_type] || 'bg-gray-100 text-gray-600'}`}>
                      {ASSET_TYPES.find((t) => t.value === asset.asset_type)?.label || asset.asset_type}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {asset.currency}{asset.sector ? ` · ${asset.sector}` : ''}{asset.country ? ` · ${asset.country}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setHistoryAsset(asset)}
                  className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition"
                  title="Price History"
                >
                  <History className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setEditingAsset(asset); setShowForm(true); }}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setConfirmDelete(asset.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showForm && (
        <AssetForm
          asset={editingAsset}
          onSubmit={editingAsset ? handleUpdate : handleCreate}
          onClose={() => { setShowForm(false); setEditingAsset(null); }}
        />
      )}

      {historyAsset && (
        <PriceHistoryModal asset={historyAsset} onClose={() => setHistoryAsset(null)} />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Asset?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will permanently delete the asset. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetsScreen;
